import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'tr', label: 'Türkçe' },
    { code: 'ru', label: 'Русский' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-2xl font-bold tracking-tight"
              style={{ color: '#1c3855' }}
            >
              WallArt Pro
            </button>
          </div>

          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('capabilities')}
              className="text-gray-700 hover:text-[#1c3855] transition-colors font-medium"
            >
              {t.nav.capabilities}
            </button>
            <button
              onClick={() => scrollToSection('technology')}
              className="text-gray-700 hover:text-[#1c3855] transition-colors font-medium"
            >
              {t.nav.technology}
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="text-gray-700 hover:text-[#1c3855] transition-colors font-medium"
            >
              {t.nav.pricing}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-[#1c3855] text-white px-6 py-2.5 rounded-lg hover:bg-[#152d44] transition-colors font-medium"
            >
              {t.nav.contact}
            </button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Globe className="h-4 w-4" />
                  <span className="hidden lg:inline">
                    {languages.find(l => l.code === language)?.label}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? 'bg-gray-100' : ''}
                  >
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <button
            className="md:hidden text-gray-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t shadow-lg">
          <div className="px-4 py-6 space-y-4">
            <button
              onClick={() => scrollToSection('capabilities')}
              className="block w-full text-left text-gray-700 hover:text-[#1c3855] py-2 font-medium"
            >
              {t.nav.capabilities}
            </button>
            <button
              onClick={() => scrollToSection('technology')}
              className="block w-full text-left text-gray-700 hover:text-[#1c3855] py-2 font-medium"
            >
              {t.nav.technology}
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="block w-full text-left text-gray-700 hover:text-[#1c3855] py-2 font-medium"
            >
              {t.nav.pricing}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-center bg-[#1c3855] text-white px-6 py-2.5 rounded-lg hover:bg-[#152d44] transition-colors font-medium"
            >
              {t.nav.contact}
            </button>
            
            <div className="pt-4 border-t">
              <div className="flex gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex-1 py-2 rounded-lg border transition-colors ${
                      language === lang.code
                        ? 'bg-[#1c3855] text-white border-[#1c3855]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#1c3855]'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
